/**
 * Step 3: Invite Members
 *
 * Story 5.2: Organization Onboarding Flow
 * Task 5: Create InviteMembersStep component
 */

import type { JSX } from 'react';
import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button, SelectItem } from '@heroui/react';

import { Input, Select } from '~/components/Field';

import type { OnboardingFormData } from './schemas';

interface InviteMembersStepProps {
  form: UseFormReturn<OnboardingFormData>;
}

const MAX_INVITES = 5;

const ROLE_OPTIONS = [
  { key: 'member', label: 'Member' },
  { key: 'admin', label: 'Admin' },
];

export function OrganizationInvite({ form }: InviteMembersStepProps): JSX.Element {
  const {
    control,
    formState: { errors },
    register,
  } = form;

  const { append, fields, remove } = useFieldArray({
    control,
    name: 'invites',
  });

  const handleAddInvite = useCallback(() => {
    if (fields.length < MAX_INVITES) {
      append({ email: '', role: 'member' });
    }
  }, [fields.length, append]);

  const handleRemoveInvite = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove],
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Invite Team Members</h2>
        <p className="text-sm">
          Invite colleagues to join your organization. You can invite up to {MAX_INVITES} members
          now, or skip this step and invite them later. This step is optional.
        </p>
      </div>

      {fields.length > 0 && (
        <div className="space-y-4">
          {fields.map((field, index) => (
            <div key={field.id} className="flex gap-3 items-start">
              <div className="flex-1">
                <Input
                  id={`invites.${index}.email`}
                  label={index === 0 ? 'Email' : undefined}
                  labelPlacement="outside-top"
                  placeholder="colleague@example.com"
                  type="email"
                  isInvalid={!!errors.invites?.[index]?.email}
                  errorMessage={errors.invites?.[index]?.email?.message}
                  {...register(`invites.${index}.email`)}
                />
              </div>
              <div className="w-32">
                {index === 0 && (
                  <label htmlFor={`invites.${index}.role`} className="block pb-2">
                    Role
                  </label>
                )}
                <Select
                  id={`invites.${index}.role`}
                  className="max-w-xs block"
                  selectedKeys={[form.watch(`invites.${index}.role`)]}
                  onSelectionChange={keys => {
                    const value = Array.from(keys)[0] as 'admin' | 'member';
                    form.setValue(`invites.${index}.role`, value);
                  }}
                  isInvalid={!!errors.invites?.[index]?.role}
                  errorMessage={errors.invites?.[index]?.role?.message}
                  items={ROLE_OPTIONS}
                  placeholder="Select a role"
                >
                  {ROLE_OPTIONS.map(option => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>
              </div>
              <Button
                isIconOnly
                variant="light"
                color="danger"
                size="sm"
                className={index === 0 ? 'mt-8' : 'mt-1'}
                onPress={() => handleRemoveInvite(index)}
                aria-label="Remove invite"
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Global array error (e.g., unique emails) */}
      {errors.invites?.root?.message && (
        <p className="text-danger text-sm">{errors.invites.root.message}</p>
      )}

      {fields.length < MAX_INVITES && (
        <Button startContent={<PlusIcon className="h-4 w-4" />} onPress={handleAddInvite}>
          Add Team Member
        </Button>
      )}

      {fields.length === 0 && (
        <div className="text-center py-8 bg-default-100 rounded-lg">
          <p>No invitations added yet</p>
        </div>
      )}
    </div>
  );
}
